import { state, saveGameData } from './state.js';
import { mpState } from './multiplayer.js';
import { drawBallSkin } from './main.js';
import { playSound, openShopSound, buySound, cannotAffordSound, activatePowerUpSound, triggerGameOverSequence, transitionMusicToMenu, playRandomFunPhrase, playPowerUpSelectSound, playBuySuccessSound, playBuyFailureSound, playMenuClickSound, playVoiceOver } from './audio.js';
import { generateSciFiName, createFireworks, saveCurrentLevel } from './level.js';
import {
    ARCADE_MODE_ID,
    RANDOM_COURSE_ID,
    POWERUP_COSTS,
    BUCKS_PER_UNDER_PAR_POINT,
    MULTI_BALL_COUNT,
    BUNKER_DEFAULT_WALL_THICKNESS
} from './constants.js';

export const COIN_ICON_SVG = `<svg class="inline-block w-4 h-4 align-middle mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 1px 1px rgba(0,0,0,0.5)); margin-top: -2.5px;"><circle cx="12" cy="12" r="10" fill="#FBBF24" stroke="#000" stroke-width="2"/><circle cx="12" cy="12" r="6" fill="#F59E0B" stroke="#000" stroke-width="1"/><path d="M12 7V14M12 7L15 9L12 10.5" stroke="#FFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;


// --- Global Leaderboard Keys ---
// Designate the existing board keys for Classic Mode (strokes) so players don't lose old records
export const DREAMLO_CLASSIC_PUBLIC_KEY = '6a18362f8f40bb17b01356a5';
export const DREAMLO_CLASSIC_PRIVATE_KEY = '6ar899hakEWRd_n3yG-wTAF4eLIDq7CkilzJjyRDjlXA';

// New free board keys for Arcade Mode (holes completed) to prevent Classic scores from overwriting them
export let DREAMLO_ARCADE_PUBLIC_KEY = '6a1d90a28f40bb17b03dc3a5';
export let DREAMLO_ARCADE_PRIVATE_KEY = 'FZSbI3--GUS1S-3tArUvag3bdRwx5y2kyiLg53XVQaIg';

export function getDreamloKeys(courseId) {
    const isArcade = courseId === ARCADE_MODE_ID || courseId === 'Arcade';
    if (isArcade) {
        if (!DREAMLO_ARCADE_PUBLIC_KEY || DREAMLO_ARCADE_PUBLIC_KEY === 'YOUR_NEW_ARCADE_PUBLIC_KEY_HERE') {
            console.warn("⚠️ Arcade leaderboard keys not configured. Falling back to Classic keys.");
            return {
                public: DREAMLO_CLASSIC_PUBLIC_KEY,
                private: DREAMLO_CLASSIC_PRIVATE_KEY
            };
        }
        return {
            public: DREAMLO_ARCADE_PUBLIC_KEY,
            private: DREAMLO_ARCADE_PRIVATE_KEY
        };
    }
    return {
        public: DREAMLO_CLASSIC_PUBLIC_KEY,
        private: DREAMLO_CLASSIC_PRIVATE_KEY
    };
}



// Variables to keep callbacks registered at boot time
let initGameCallback = null;
let loadNextLevelCallback = null;
let resetBallToStartLocationCallback = null;
let updatePowerUpUIDisplayCallback = null;

export function registerUICallbacks(initGame, loadNextLevel, resetBall, updatePowerUpUI) {
    initGameCallback = initGame;
    loadNextLevelCallback = loadNextLevel;
    resetBallToStartLocationCallback = resetBall;
    updatePowerUpUIDisplayCallback = updatePowerUpUI;
}

// --- Player Name Management ---
export function updatePlayerName(newName) {
    newName = newName.trim();
    if (newName && newName.length > 0 && newName.length <= 20) { 
        state.currentPlayerName = newName;
        localStorage.setItem('spaceGolfPlayerName', state.currentPlayerName);
        console.log(`Player name set to: ${state.currentPlayerName}`);
        const nameInput = document.getElementById('playerNameInputSettings');
        if (nameInput) nameInput.value = state.currentPlayerName;
        return true;
    }
    alert("Player name must be 1-20 characters.");
    const nameInput = document.getElementById('playerNameInputSettings');
    if (nameInput) nameInput.value = state.currentPlayerName; 
    return false;
}

// --- Callsign Gating Modal Logic ---
let pendingNameRegistrationCallback = null;

export function ensurePlayerNameRegistered(onSuccess) {
    const isDefaultName = state.currentPlayerName === "Player";
    const isEmptyName = !state.currentPlayerName || state.currentPlayerName.trim().length === 0;

    if (!isDefaultName && !isEmptyName) {
        if (onSuccess) onSuccess();
        return;
    }

    pendingNameRegistrationCallback = onSuccess;
    const nameModal = document.getElementById('playerNamePromptModal');
    const nameInput = document.getElementById('playerNamePromptInput');
    
    if (nameInput) {
        nameInput.value = state.currentPlayerName === "Player" ? "" : state.currentPlayerName;
    }
    if (nameModal) {
        nameModal.classList.add('active');
        // Pre-select saved custom color, skin, and trail
        if (typeof window.syncColorHighlight === 'function') {
            window.syncColorHighlight(state.customBallColor, state.customBallSkin, state.customBallTrail);
        }
    }
}

let tempSelectedColor = "#FFFFFF";
let tempSelectedSkin = "golf";
let tempSelectedTrail = "neon";

const buttonImpactTimers = {
    golf: 0,
    rave: 0,
    cat: 0,
    alien: 0
};

let skinCanvasesAnimationId = null;

export function startSkinCanvasesLoop() {
    if (skinCanvasesAnimationId) return; // already running
    
    const canvasIds = {
        golf: 'skinCanvasGolf',
        rave: 'skinCanvasRave',
        cat: 'skinCanvasCat',
        alien: 'skinCanvasAlien'
    };
    
    function tick() {
        const modal = document.getElementById('playerNamePromptModal');
        if (!modal || !modal.classList.contains('active')) {
            skinCanvasesAnimationId = null;
            return; // stop loop when modal is closed
        }
        
        // Convert temp selected hex color to RGB
        let colorHex = tempSelectedColor || "#FFFFFF";
        if (colorHex.startsWith("#")) colorHex = colorHex.slice(1);
        let cr = 255, cg = 255, cb = 255;
        if (colorHex.length === 6) {
            cr = parseInt(colorHex.substring(0, 2), 16);
            cg = parseInt(colorHex.substring(2, 4), 16);
            cb = parseInt(colorHex.substring(4, 6), 16);
        }
        
        // Loop over the 4 skins and draw each one on its little canvas
        Object.entries(canvasIds).forEach(([skinKey, canvasId]) => {
            const canvas = document.getElementById(canvasId);
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            
            // Decrement local click impact timer
            if (buttonImpactTimers[skinKey] > 0) {
                buttonImpactTimers[skinKey]--;
            }
            
            ctx.clearRect(0, 0, 48, 48);
            
            // Draw ball at center (24, 24) with radius 14
            // Keep rotation at 0 (straight up) so the face is perfectly clear!
            drawBallSkin(ctx, 24, 24, 14, 0, skinKey, cr, cg, cb, buttonImpactTimers[skinKey]);
        });
        
        skinCanvasesAnimationId = requestAnimationFrame(tick);
    }
    
    skinCanvasesAnimationId = requestAnimationFrame(tick);
}

export function setupPlayerNamePromptModalListeners() {
    const randomBtn = document.getElementById('playerNamePromptRandomBtn');
    const cancelBtn = document.getElementById('playerNamePromptCancelBtn');
    const confirmBtn = document.getElementById('playerNamePromptConfirmBtn');
    const nameInput = document.getElementById('playerNamePromptInput');
    const nameModal = document.getElementById('playerNamePromptModal');

    // Custom color highlights
    const colorIds = ['White', 'Cyan', 'Pink', 'Green', 'Gold', 'Orange', 'Purple'];
    function highlightSelectedColor(colorHex) {
        colorIds.forEach(id => {
            const btn = document.getElementById(`colorBtn${id}`);
            if (btn) {
                const btnColor = btn.dataset.color;
                if (btnColor === colorHex) {
                    btn.classList.add('ring-4', 'ring-cyan-300', 'scale-110');
                } else {
                    btn.classList.remove('ring-4', 'ring-cyan-300', 'scale-110');
                }
            }
        });
    }

    const skinIds = ['Golf', 'Rave', 'Cat', 'Alien'];
    function highlightSelectedSkin(skinValue) {
        skinIds.forEach(id => {
            const btn = document.getElementById(`skinBtn${id}`);
            if (btn) {
                const btnSkin = btn.dataset.skin;
                if (btnSkin === skinValue) {
                    btn.classList.add('border-cyan-400', 'scale-110', 'ring-2', 'ring-cyan-400/40', 'bg-slate-800');
                    btn.classList.remove('border-black', 'bg-slate-900');
                } else {
                    btn.classList.remove('border-cyan-400', 'scale-110', 'ring-2', 'ring-cyan-400/40', 'bg-slate-800');
                    btn.classList.add('border-black', 'bg-slate-900');
                }
            }
        });
    }

    const trailIds = ['Neon', 'Rainbow', 'Stardust'];
    function highlightSelectedTrail(trailValue) {
        trailIds.forEach(id => {
            const btn = document.getElementById(`trailBtn${id}`);
            if (btn) {
                const btnTrail = btn.dataset.trail;
                if (btnTrail === trailValue) {
                    btn.classList.add('button-primary');
                    btn.classList.remove('button-secondary');
                } else {
                    btn.classList.add('button-secondary');
                    btn.classList.remove('button-primary');
                }
            }
        });
    }

    // Register globally to trigger on modal open
    window.syncColorHighlight = (colorHex, skinValue, trailValue) => {
        tempSelectedColor = colorHex || state.customBallColor || "#FFFFFF";
        tempSelectedSkin = skinValue || state.customBallSkin || "golf";
        tempSelectedTrail = trailValue || state.customBallTrail || "neon";
        highlightSelectedColor(tempSelectedColor);
        highlightSelectedSkin(tempSelectedSkin);
        highlightSelectedTrail(tempSelectedTrail);
        startSkinCanvasesLoop();
    };

    // Attach click listeners to all custom color buttons
    colorIds.forEach(id => {
        const btn = document.getElementById(`colorBtn${id}`);
        if (btn) {
            btn.addEventListener('click', () => {
                playMenuClickSound();
                tempSelectedColor = btn.dataset.color;
                highlightSelectedColor(tempSelectedColor);
            });
        }
    });

    // Attach click listeners to all custom skin buttons
    skinIds.forEach(id => {
        const btn = document.getElementById(`skinBtn${id}`);
        if (btn) {
            btn.addEventListener('click', () => {
                playMenuClickSound();
                tempSelectedSkin = btn.dataset.skin;
                highlightSelectedSkin(tempSelectedSkin);
                // Trigger rapid squinty face squish micro-animation on clicked button canvas (lengthened to 35 frames!)
                buttonImpactTimers[tempSelectedSkin] = 35;
            });
        }
    });

    // Attach click listeners to all custom trail buttons
    trailIds.forEach(id => {
        const btn = document.getElementById(`trailBtn${id}`);
        if (btn) {
            btn.addEventListener('click', () => {
                playMenuClickSound();
                tempSelectedTrail = btn.dataset.trail;
                highlightSelectedTrail(tempSelectedTrail);
            });
        }
    });

    if (randomBtn) {
        randomBtn.addEventListener('click', () => {
            playMenuClickSound();
            const sciFiName = generateSciFiName();
            if (nameInput) nameInput.value = sciFiName;
        });
    }

    if (cancelBtn) {
        cancelBtn.addEventListener('click', () => {
            playMenuClickSound();
            if (nameModal) nameModal.classList.remove('active');
            pendingNameRegistrationCallback = null;
        });
    }

    if (confirmBtn) {
        confirmBtn.addEventListener('click', () => {
            if (!nameInput) return;
            const enteredName = nameInput.value.trim();
            if (enteredName.toLowerCase() === 'player') {
                alert("Please enter a custom player name (cannot be 'Player').");
                return;
            }
            if (enteredName.length > 0 && enteredName.length <= 20) {
                updatePlayerName(enteredName);
                
                // Save custom color, skin, and trail!
                state.customBallColor = tempSelectedColor;
                state.customBallSkin = tempSelectedSkin;
                state.customBallTrail = tempSelectedTrail;
                saveGameData();

                // Trigger WebRTC multiplayer profile synchronization if connected
                if (typeof window.syncPlayerCustomizationToMultiplayer === 'function') {
                    window.syncPlayerCustomizationToMultiplayer();
                }

                playBuySuccessSound();
                if (nameModal) nameModal.classList.remove('active');
                if (pendingNameRegistrationCallback) {
                    const callback = pendingNameRegistrationCallback;
                    pendingNameRegistrationCallback = null;
                    callback();
                }
            } else {
                alert("Player name must be 1-20 characters.");
            }
        });
    }
}

// --- In-Game UI Updates ---
let lastUIState = {
    currentPlayingMode: null,
    totalScore: null,
    currentHole: null,
    currentHoleIndex: null,
    isBotGame: null,
    botDifficulty: null,
    shotCount: null,
    currentPar: null,
    totalSpaceBucks: null,
    totalMultiplayerStrokes: null,
    isArcadeRunOver: null,
    goalReached: null,
    multiBallActiveOnScreen: null,
    lastShotDistanceTraveled: null,
    ballMoving: null,
    ballIsSinking: null,
    isAiming: null,
    opponentStrokes: null,
    opponentHoleIndex: null,
    customBallColor: null,
    customBallSkin: null,
    customBallTrail: null,
    playerName: null
};

export function updateInGameUIDisplay() {
    const primaryBall = state.gameState?.balls?.[0];
    const ballMoving = primaryBall ? primaryBall.moving : false;
    const ballIsSinking = primaryBall ? primaryBall.isSinking : false;
    const opponentStrokes = mpState.opponentProgress ? mpState.opponentProgress.strokes : 0;
    const opponentHoleIndex = mpState.opponentProgress ? mpState.opponentProgress.holeIndex : 0;

    const currentUIState = {
        currentPlayingMode: state.currentPlayingMode,
        totalScore: state.totalScore,
        currentHole: state.currentHole,
        currentHoleIndex: state.currentHoleIndex,
        isBotGame: state.isBotGame,
        botDifficulty: state.botDifficulty,
        shotCount: state.shotCount,
        currentPar: state.currentPar,
        totalSpaceBucks: state.totalSpaceBucks,
        totalMultiplayerStrokes: state.totalMultiplayerStrokes,
        isArcadeRunOver: state.isArcadeRunOver,
        goalReached: state.gameState?.goalReached || false,
        multiBallActiveOnScreen: state.gameState?.multiBallActiveOnScreen || false,
        lastShotDistanceTraveled: state.gameState?.lastShotDistanceTraveled || 0,
        ballMoving,
        ballIsSinking,
        isAiming: state.gameState?.isAiming || false,
        opponentStrokes,
        opponentHoleIndex,
        customBallColor: state.customBallColor,
        customBallSkin: state.customBallSkin,
        customBallTrail: state.customBallTrail,
        playerName: state.currentPlayerName
    };

    let hasChanged = false;
    for (const key in currentUIState) {
        if (currentUIState[key] !== lastUIState[key]) {
            hasChanged = true;
            break;
        }
    }
    if (!hasChanged) {
        return;
    }
    lastUIState = currentUIState;

    const statsHoleDisplay = document.getElementById('statsHoleDisplay');
    const statsModeBadge = document.getElementById('statsModeBadge');
    
    const spStatsContainer = document.getElementById('spStatsContainer');
    const mpStatsContainer = document.getElementById('mpStatsContainer');
    
    const spParVal = document.getElementById('spParVal');
    const spShotsVal = document.getElementById('spShotsVal');
    const spBucksRow = document.getElementById('spBucksRow');
    const spBucksVal = document.getElementById('spBucksVal');
    
    const cardFinalShotWarning = document.getElementById('cardFinalShotWarning');
    const largeFinalShotAlert = document.getElementById('largeFinalShotAlert');
    
    const lastShotInfo = document.getElementById('lastShotInfo');
    const arcadeHighScoreInfoEl = document.getElementById('arcadeHighScoreInfo');
    const courseNameInfo = document.getElementById('courseNameInfo');

    const isBallStationary = primaryBall && !primaryBall.moving && !primaryBall.isSinking && !state.gameState.goalReached && !state.isArcadeRunOver;

    // Check final shot condition: we have exactly 1 shot left! (Only applicable in Arcade mode)
    // Left card warning stays visible while aiming or stationary
    const isFinalShotLeft = state.currentPlayingMode === 'powerup' && state.shotCount === state.currentPar - 1 && isBallStationary;
    // Center warning fades away as soon as aiming starts
    const isFinalShotCenter = isFinalShotLeft && !state.gameState.isAiming;

    if (isFinalShotLeft) {
        if (cardFinalShotWarning) cardFinalShotWarning.style.display = 'block';
    } else {
        if (cardFinalShotWarning) cardFinalShotWarning.style.display = 'none';
    }

    if (isFinalShotCenter) {
        if (largeFinalShotAlert) largeFinalShotAlert.style.opacity = '1';
    } else {
        if (largeFinalShotAlert) largeFinalShotAlert.style.opacity = '0';
    }

    const statsBestDisplay = document.getElementById('statsBestDisplay');

    if (state.currentPlayingMode === 'powerup') {
        if (statsHoleDisplay) statsHoleDisplay.textContent = `HOLE: ${state.totalScore + 1}`;
        if (statsModeBadge) {
            const arcadeScores = state.courseHighScores[ARCADE_MODE_ID];
            const bestHoles = (arcadeScores && arcadeScores.length > 0) ? arcadeScores[0].holesCompleted : 0;
            statsModeBadge.textContent = `Arcade | Best ${bestHoles}`;
            statsModeBadge.className = 'pointer-events-auto cursor-pointer text-[9px] bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-0.5 rounded-full uppercase';
        }
        if (spStatsContainer) spStatsContainer.style.display = 'block';
        if (mpStatsContainer) mpStatsContainer.style.display = 'none';
        if (spParVal) spParVal.textContent = state.currentPar;
        if (spShotsVal) spShotsVal.textContent = state.shotCount;
        if (spBucksRow) spBucksRow.style.display = 'inline-block';
        if (spBucksVal) spBucksVal.textContent = state.totalSpaceBucks;
        
        if (arcadeHighScoreInfoEl) arcadeHighScoreInfoEl.style.display = 'none';
        if (statsBestDisplay) statsBestDisplay.style.display = 'none';
    } else if (state.currentPlayingMode === 'multiplayer') {
        if (statsHoleDisplay) {
            if (state.currentHoleIndex + 1 === 9) {
                statsHoleDisplay.textContent = "FINAL HOLE";
            } else {
                statsHoleDisplay.textContent = `HOLE ${state.currentHoleIndex + 1}/9`;
            }
        }
        if (statsModeBadge) {
            let diffLabel = "ONLINE";
            if (state.isBotGame) {
                diffLabel = state.botDifficulty === 1 ? "EASY" : (state.botDifficulty === 2 ? "MEDIUM" : "HARD");
            }
            statsModeBadge.textContent = `Speedrun | ${diffLabel}`;
            statsModeBadge.className = 'pointer-events-auto cursor-pointer text-[9px] bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 px-2 py-0.5 rounded-full uppercase';
        }
        if (spStatsContainer) spStatsContainer.style.display = 'none';
        if (mpStatsContainer) {
            mpStatsContainer.style.display = 'block';
            const mpLocalDifficulty = document.getElementById('mpLocalDifficulty');
            if (mpLocalDifficulty) {
                let diffLabel = "ONLINE";
                let diffClass = "text-cyan-400";
                if (state.isBotGame) {
                    if (state.botDifficulty === 1) {
                        diffLabel = "EASY";
                        diffClass = "text-green-400";
                    } else if (state.botDifficulty === 2) {
                        diffLabel = "MEDIUM";
                        diffClass = "text-yellow-400";
                    } else if (state.botDifficulty === 3) {
                        diffLabel = "HARD";
                        diffClass = "text-rose-400";
                    }
                }
                mpLocalDifficulty.textContent = diffLabel;
                mpLocalDifficulty.className = `font-black uppercase tracking-wider ${diffClass}`;
            }
        }
        
        // Update multiplayer values
        const localCurrentShots = document.getElementById('mpLocalCurrentShots');
        const localScoreVal = document.getElementById('mpLocalScoreVal');
        const localHoleLabel = document.getElementById('mpLocalHoleLabel');
        const opponentScoreVal = document.getElementById('mpOpponentScoreVal');
        const opponentHoleLabel = document.getElementById('mpOpponentHoleLabel');
        
        if (localCurrentShots) localCurrentShots.textContent = state.shotCount;
        if (localScoreVal) localScoreVal.textContent = state.totalMultiplayerStrokes + state.shotCount;
        if (localHoleLabel) localHoleLabel.textContent = `(H${state.currentHoleIndex + 1})`;
        
        if (opponentScoreVal) opponentScoreVal.textContent = mpState.opponentProgress ? mpState.opponentProgress.strokes : 0;
        if (opponentHoleLabel) opponentHoleLabel.textContent = mpState.opponentProgress ? `(H${mpState.opponentProgress.holeIndex + 1})` : `(H1)`;
        
        if (arcadeHighScoreInfoEl) arcadeHighScoreInfoEl.style.display = 'none';
        if (statsBestDisplay) statsBestDisplay.style.display = 'none';
        if (spBucksRow) spBucksRow.style.display = 'none';
    } else {
        let holeDisplayName = state.currentHoleName ? ` - ${state.currentHoleName}` : "";
        if (statsHoleDisplay) {
            if (state.currentHole + 1 === state.TOTAL_HOLES) {
                statsHoleDisplay.textContent = "FINAL HOLE";
            } else {
                statsHoleDisplay.textContent = `HOLE ${state.currentHole + 1}/${state.TOTAL_HOLES}`;
            }
        }
        if (statsModeBadge) {
            const completedPar = state.totalCoursePar - state.currentPar;
            const currentRelative = state.totalScore - completedPar;
            const formattedRelative = currentRelative === 0 ? "EVEN" : (currentRelative > 0 ? `+${currentRelative}` : `${currentRelative}`);
            
            const classicScores = state.courseHighScores[RANDOM_COURSE_ID];
            let bestText = "";
            if (classicScores && classicScores.length > 0) {
                const bestRelative = classicScores[0].score - (classicScores[0].par || 27);
                const formattedBest = bestRelative === 0 ? "EVEN" : (bestRelative > 0 ? `+${bestRelative}` : `${bestRelative}`);
                bestText = ` | Best ${formattedBest}`;
            }
            
            const modeName = state.isPlayingCustomCourse ? 'Custom' : 'Classic';
            statsModeBadge.textContent = `${modeName} | ${formattedRelative}${bestText}`;
            statsModeBadge.className = 'pointer-events-auto cursor-pointer text-[9px] bg-green-500/20 text-green-400 border border-green-500/30 px-2 py-0.5 rounded-full uppercase';
        }
        if (spStatsContainer) spStatsContainer.style.display = 'block';
        if (mpStatsContainer) mpStatsContainer.style.display = 'none';
        
        if (spParVal) {
            spParVal.textContent = state.currentPar;
            const courseParLabel = document.getElementById('spCourseParLabel');
            if (courseParLabel) {
                courseParLabel.style.display = 'none';
            }
        }
        
        if (spShotsVal) spShotsVal.textContent = state.shotCount;
        if (spBucksRow) spBucksRow.style.display = 'none';
        if (arcadeHighScoreInfoEl) arcadeHighScoreInfoEl.style.display = 'none';
        if (statsBestDisplay) statsBestDisplay.style.display = 'none';
    }

    if (courseNameInfo) {
        courseNameInfo.style.display = 'none'; // Hidden per user request: "dont need 9 hole classic text on right side"
    }
    
    if (state.gameState.lastShotDistanceTraveled > 0 && !state.gameState.goalReached && primaryBall && !primaryBall.moving && !primaryBall.isSinking && state.currentPlayingMode !== 'multiplayer') {
        if (lastShotInfo) {
            lastShotInfo.textContent = `Last Shot: ${state.gameState.lastShotDistanceTraveled.toFixed(0)} IAU`;
            lastShotInfo.style.display = 'block';
        }
    } else {
        if (lastShotInfo) lastShotInfo.style.display = 'none';
    }
    updatePowerUpActivationButtons(); 
}

export function showGoalScreen() {
    if (state.currentPlayingMode === 'powerup' && state.isArcadeRunOver) return; 

    // Reset goal message styling to standard comic font
    const goalMsgEl = document.getElementById('goalMessage');
    if (goalMsgEl) {
        goalMsgEl.className = "text-3xl font-bold mb-3";
    }

    const bucksEarnedInfoGoal = document.getElementById('bucksEarnedInfoGoal');
    if (state.currentPlayingMode === 'powerup' && state.shotCount > state.currentPar) { 
        console.log(`Arcade Game Over (goal reached over par): Hole ${state.totalScore + 1} completed in ${state.shotCount} shots (Par ${state.currentPar}).`);
        triggerArcadeGameOver(); 
        return; 
    }

    if (state.hasBouncedOffRockInCurrentHole === false) {
        triggerAchievement('pacifist');
    }

    // Calculate hole-level flight efficiency rating
    const holeEfficiency = state.gameState.currentHoleDistanceTraveled > 0 ? Math.min(100, Math.round((state.currentHoleOptimalDistance / state.gameState.currentHoleDistanceTraveled) * 100)) : 100;

    let bucksEarnedThisHole = 0;
    if (state.currentPlayingMode === 'powerup') {
        // Multi-factor point system: 10 base + par stroke bonus + efficiency rating bonus
        const basePoints = 10;
        const scoreDiff = state.currentPar - state.shotCount;
        let parBonus = 0;
        if (scoreDiff > 0) {
            parBonus = scoreDiff * BUCKS_PER_UNDER_PAR_POINT;
        } else if (scoreDiff === 0) {
            parBonus = 2; // +2 bonus for meeting par exactly
        }
        const efficiencyBonus = Math.round(holeEfficiency * 0.1); // up to 10 extra coins for 100% efficiency

        bucksEarnedThisHole = basePoints + parBonus + efficiencyBonus;
        state.totalSpaceBucks += bucksEarnedThisHole;
        state.totalScore++; 
        saveGameData(); 
        bucksEarnedInfoGoal.innerHTML = `+${bucksEarnedThisHole} ${COIN_ICON_SVG}`;
        bucksEarnedInfoGoal.style.display = 'inline-block';
        
        if (state.shotCount === 1) {
            const phrases = [
                "一撃必殺！ ONE SHOT! 🌟",
                "奇跡のショット！ MIRACLE SHOT! 🌟",
                "神業！ DIVINE SKILL! 🌟",
                "完璧！ PERFECT! 🌟",
                "超スゴい！ SUPER AWESOME! 🌟",
                "ホールインワン！ HOLE IN ONE! 🌟"
            ];
            const jpSpeech = [
                "一撃必殺！",
                "奇跡のショット！",
                "神業！",
                "完璧！",
                "超スゴい！",
                "ホールインワン！"
            ];
            const selectedIdx = Math.floor(Math.random() * phrases.length);
            document.getElementById('goalMessage').textContent = phrases[selectedIdx];
            if (goalMsgEl) {
                goalMsgEl.className = "text-3xl font-black mb-3 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-400 to-amber-600 animate-pulse tracking-wide select-none drop-shadow-[0_4px_10px_rgba(245,158,11,0.5)]";
            }
            if (typeof window.triggerMassiveHoleInOneCelebration === 'function') {
                window.triggerMassiveHoleInOneCelebration();
            }
            playVoiceOver(jpSpeech[selectedIdx]);
        } else {
            document.getElementById('goalMessage').textContent = `アーケード・ホール ${state.totalScore} クリア！`;
        }
    } else { 
        bucksEarnedInfoGoal.style.display = 'none';
        let message; 
        const scoreRelPar = state.shotCount - state.currentPar;
        if (state.shotCount === 1) {
            const phrases = [
                "一撃必殺！ ONE SHOT! 🌟",
                "奇跡のショット！ MIRACLE SHOT! 🌟",
                "神業！ DIVINE SKILL! 🌟",
                "完璧！ PERFECT! 🌟",
                "超スゴい！ SUPER AWESOME! 🌟",
                "ホールインワン！ HOLE IN ONE! 🌟"
            ];
            const jpSpeech = [
                "一撃必殺！",
                "奇跡のショット！",
                "神業！",
                "完璧！",
                "超スゴい！",
                "ホールインワン！"
            ];
            const selectedIdx = Math.floor(Math.random() * phrases.length);
            message = phrases[selectedIdx];
            
            if (goalMsgEl) {
                goalMsgEl.className = "text-3xl font-black mb-3 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-400 to-amber-600 animate-pulse tracking-wide select-none drop-shadow-[0_4px_10px_rgba(245,158,11,0.5)]";
            }
            if (typeof window.triggerMassiveHoleInOneCelebration === 'function') {
                window.triggerMassiveHoleInOneCelebration();
            }
            playVoiceOver(jpSpeech[selectedIdx]);
        } else if (scoreRelPar === -3) { 
            message = "セレスティアル・アルバトロス！";
        } else if (scoreRelPar === -2) { 
            message = "オリオン・イーグル！";
        } else if (scoreRelPar === -1) { 
            message = "スター・バーディ！";
        } else if (scoreRelPar === 0) { 
            message = "コズミック・パー！";
        } else if (scoreRelPar === 1) { 
            message = "アステロイド・ボギー！";
        } else if (scoreRelPar === 2) { 
            message = "ダブル・ボギー！";
        } else if (scoreRelPar === 3) { 
            message = "トリプル・ボギー！";
        } else if (scoreRelPar > 3) {
            message = "ロスト・イン・スペース…";
        } else { 
            message = "ナイスショット！"; 
        }
        document.getElementById('goalMessage').textContent = message;
    }

    document.getElementById('shotCount').textContent = `${state.shotCount}`;
    document.getElementById('parInfoGoal').textContent = `${state.currentPar}`;
    document.getElementById('optimalDistanceGoal').textContent = `${state.currentHoleOptimalDistance.toFixed(0)} IAU`;
    document.getElementById('totalDistanceGoal').textContent = `${state.gameState.currentHoleDistanceTraveled.toFixed(0)} IAU`;
    
    // Set efficiency display on the scorecard
    const accuracyGoalEl = document.getElementById('accuracyGoal');
    if (accuracyGoalEl) {
        accuracyGoalEl.textContent = `${holeEfficiency}%`;
    }
    
    const goalShopBtn = document.getElementById('goalScreenShopBtn');
    if (goalShopBtn) {
        if (state.currentPlayingMode === 'powerup') {
            goalShopBtn.style.display = 'inline-flex';
        } else {
            goalShopBtn.style.display = 'none';
        }
    }
    
    const nextButton = document.getElementById('nextLevel');
    if (state.currentPlayingMode === 'powerup') {
        nextButton.textContent = 'Next Hole';
    } else {
        nextButton.textContent = state.currentHole === state.TOTAL_HOLES - 1 ? 'Finish Course & View Score' : `Next Hole (${state.currentHole + 2}/${state.TOTAL_HOLES})`;
    }
    
    document.getElementById('goalScreen').classList.add('active');
    
    // Muffle bitcrushed soundtrack back in during level clear intermission
    transitionMusicToMenu();
    
    if (state.currentPlayingMode === 'powerup' && updatePowerUpUIDisplayCallback) {
        updatePowerUpUIDisplayCallback();
    }
}

export function triggerArcadeGameOver() {
    if (state.isArcadeRunOver) return; 
    state.isArcadeRunOver = true;

    // Play retro chiptune and vocal "GAME OVER!" shout
    triggerGameOverSequence();

    document.getElementById('goalScreen').classList.remove('active'); 
    const gameOverScreen = document.getElementById('gameOverScreen');
    
    document.getElementById('gameOverTitle').textContent = "Game Over";
    
    // Hide Classic-only elements
    document.getElementById('rowFinalScore').style.display = 'none';
    
    // Set labels & values
    document.getElementById('lblFinalShots').textContent = "Holes Completed";
    document.getElementById('txtFinalShots').textContent = state.totalScore;
    
    document.getElementById('lblCoursePar').textContent = "Total Par";
    document.getElementById('txtCoursePar').textContent = state.totalCoursePar;
    
    // Compute and display round efficiency (0% if no holes completed in Arcade)
    const finalEfficiency = (state.totalScore > 0 && state.totalCourseDistance > 0) ? Math.min(100, Math.round((state.totalCourseOptimalDistance / state.totalCourseDistance) * 100)) : 0;
    document.getElementById('txtFinalAccuracy').textContent = `${finalEfficiency}%`;
    
    // Hide coins earned on Arcade final screen per request
    document.getElementById('rowFinalBucks').style.display = 'none';
    
    document.getElementById('txtOptimalDistance').textContent = `${state.totalCourseOptimalDistance.toFixed(0)} IAU`;
    document.getElementById('txtActualDistance').textContent = `${state.totalCourseDistance.toFixed(0)} IAU`;

    // Save high score first (so it gets ranked correctly)
    saveHighScore(ARCADE_MODE_ID, state.totalScore, state.totalCoursePar, state.totalCourseDistance, state.totalCourseOptimalDistance, state.currentPlayerName);
    
    // Query rank and display dynamically inside the player stamp using our centralized rank stamp helper
    updateGameOverRankStamp(ARCADE_MODE_ID, state.totalScore, state.totalCoursePar, state.totalCourseDistance);
    
    const restartBtn = document.getElementById('restartGame');
    restartBtn.textContent = 'Play Again (Arcade)';
    
    gameOverScreen.classList.add('active');
    
    // Sweep background music back in unmuffled on Game Over screen
    transitionMusicToMenu();
}

// --- Power-Ups Shop and Activations ---
export function updatePowerUpShopDisplay() {
    document.getElementById('shopSpaceBucksDisplay').textContent = state.totalSpaceBucks;
    
    const items = ['gravityGrid', 'trajectoryLengthener', 'stickyBall', 'linearLaser', 'chronoRewind', 'multiBall', 'oobShield'];
    items.forEach(type => {
        const ownedEl = document.getElementById(`owned${type.charAt(0).toUpperCase() + type.slice(1)}`);
        if (ownedEl) ownedEl.textContent = state.ownedPowerUps[type];
        
        const buyBtn = document.getElementById(`buy${type.charAt(0).toUpperCase() + type.slice(1)}Btn`);
        if (buyBtn) {
            buyBtn.disabled = state.totalSpaceBucks < POWERUP_COSTS[type];
            buyBtn.classList.toggle('opacity-50', state.totalSpaceBucks < POWERUP_COSTS[type]);
        }
    });
}

export function buyPowerUp(type) {
    if (state.totalSpaceBucks >= POWERUP_COSTS[type]) {
        state.totalSpaceBucks -= POWERUP_COSTS[type];
        state.ownedPowerUps[type]++;
        saveGameData(); 
        updatePowerUpShopDisplay();
        if (updatePowerUpUIDisplayCallback) updatePowerUpUIDisplayCallback();
        updatePowerUpActivationButtons();
        playBuySuccessSound();
        playRandomFunPhrase();
        console.log(`Bought 1 ${type}. Total ${state.ownedPowerUps[type]}. Bucks left: ${state.totalSpaceBucks}`);
    } else {
        playBuyFailureSound();
        console.log(`Cannot afford ${type}. Cost: ${POWERUP_COSTS[type]}, Have: ${state.totalSpaceBucks}`);
    }
}

export function updatePowerUpUIDisplay() { 
    if (state.currentPlayingMode === 'powerup') {
        const spBucksVal = document.getElementById('spBucksVal');
        if (spBucksVal) spBucksVal.textContent = `${state.totalSpaceBucks} SB`;
    }
}

export function updatePowerUpActivationButtons() {
    const items = ['gravityGrid', 'trajectoryLengthener', 'stickyBall', 'linearLaser', 'multiBall', 'chronoRewind', 'oobShield'];
    const primaryBall = state.gameState.balls[0];
    const isMoving = primaryBall && primaryBall.moving;
    const canActivateBase = primaryBall && !primaryBall.moving && !primaryBall.isSinking && !state.gameState.goalReached && !state.isArcadeRunOver;

    items.forEach(type => {
        const btn = document.getElementById(`activate${type.charAt(0).toUpperCase() + type.slice(1)}Btn`);
        if (!btn) return;

        // Count of owned powerups
        const owned = state.ownedPowerUps[type] || 0;
        const active = state.activePowerUps[type] || false;

        // Label update
        let label = '';
        if (type === 'trajectoryLengthener') label = `🎯 Aim (${owned})`;
        else if (type === 'gravityGrid') label = `🪐 Grid (${owned})`;
        else if (type === 'stickyBall') label = `🍯 Sticky (${owned})`;
        else if (type === 'linearLaser') label = `⚡ Laser (${owned})`;
        else if (type === 'multiBall') label = `☄️ Multi (${owned})`;
        else if (type === 'chronoRewind') label = `⏳ Rewind (${owned})`;
        else if (type === 'oobShield') label = `🛡️ Deflector (${owned})`;

        btn.innerHTML = label;

        let isUsable = false;
        if (type === 'chronoRewind') {
            // Rewind is ONLY usable when the ball is STOPPED AND a shot has been recorded!
            isUsable = !isMoving && state.rewindState !== null && owned > 0 && !state.isArcadeRunOver;
        } else if (type === 'gravityGrid') {
            // Gravity Grid is toggled on/off pre-shot
            isUsable = canActivateBase && (owned > 0 || active);
        } else {
            // Pre-shot power-ups are ONLY usable when the ball is STATIONARY!
            isUsable = canActivateBase && (owned > 0 || active);
        }

        // Apply visual usable glow class (.usable-hud-powerup) vs locked class (.locked-hud-powerup)
        if (isUsable) {
            btn.disabled = false;
            btn.classList.add('usable-hud-powerup');
            btn.classList.remove('locked-hud-powerup');
        } else {
            btn.disabled = true;
            btn.classList.remove('usable-hud-powerup');
            btn.classList.add('locked-hud-powerup');
        }

        // Maintain active armed highlight state
        btn.classList.toggle('active-powerup', active);

        // Hide from the power-up bar any powerups not owned (unless active/armed)
        if (owned > 0 || active) {
            btn.style.display = '';
        } else {
            btn.style.display = 'none';
        }
    });
}

export function togglePowerUp(type) {
    const primaryBall = state.gameState.balls[0];
    
    // Tapping Chrono Rewind (Post-shot execution)
    if (type === 'chronoRewind') {
        if (!primaryBall || primaryBall.moving) return;
        if (state.ownedPowerUps.chronoRewind > 0 && state.rewindState !== null) {
            state.ownedPowerUps.chronoRewind--;
            saveGameData();
            updatePowerUpShopDisplay();
            if (updatePowerUpUIDisplayCallback) updatePowerUpUIDisplayCallback();
            
            // Call rewind handler in main.js
            if (typeof window.triggerChronoRewind === 'function') {
                window.triggerChronoRewind();
            }
        }
        return;
    }

    // Pre-shot toggles
    if (!primaryBall || primaryBall.moving || primaryBall.isSinking || state.gameState.goalReached || state.isArcadeRunOver) {
        console.log(`Cannot activate/deactivate ${type} now.`);
        return; 
    }

    if (state.activePowerUps[type]) {
        state.activePowerUps[type] = false;
        playPowerUpSelectSound(type, false);
        console.log(`${type} deactivated.`);
    } else {
        if (state.ownedPowerUps[type] > 0) {
            // Deactivate conflicting exclusive armed items
            if (type === 'stickyBall') { state.activePowerUps.linearLaser = false; state.activePowerUps.multiBall = false; }
            if (type === 'linearLaser') { state.activePowerUps.stickyBall = false; state.activePowerUps.multiBall = false; }
            if (type === 'multiBall') { state.activePowerUps.stickyBall = false; state.activePowerUps.linearLaser = false; }
            
            state.activePowerUps[type] = true;
            playPowerUpSelectSound(type, true);
            console.log(`${type} activated.`);
        } else {
            console.log(`No ${type} power-ups owned.`);
            playSound(cannotAffordSound, "D3", "8n", Tone.now()); 
        }
    }
    updatePowerUpActivationButtons();
}

// --- High Scores ---
// --- High Scores ---
const getTime = (entry) => {
    if (entry.timestamp !== undefined) return entry.timestamp;
    if (entry.date) {
        const parsed = Date.parse(entry.date);
        if (!isNaN(parsed)) return parsed;
    }
    return 0;
};

export function addHighScoreToCachedGlobalEntries(courseId, scoreValue, parValue, totalDist, optimalDist, playerName) {
    let cachedEntries = getCachedEntriesForCourse(courseId);
    if (!cachedEntries) {
        cachedEntries = { dreamlo: { leaderboard: { entry: [] } } };
    }
    
    const isArcade = courseId === ARCADE_MODE_ID || courseId === 'Arcade';
    const globalName = isArcade ? `${playerName} _ARCADE` : playerName;
    const dreamloScore = isArcade ? scoreValue : Math.max(0, 10000 - scoreValue);
    const textData = [courseId, scoreValue, parValue, totalDist.toFixed(0), optimalDist.toFixed(0), Date.now()].join('|');
    
    const entryData = {
        name: encodeURIComponent(globalName),
        score: dreamloScore.toString(),
        seconds: "0",
        text: encodeURIComponent(textData),
        date: new Date().toUTCString()
    };
    
    if (!cachedEntries.dreamlo) {
        cachedEntries.dreamlo = { leaderboard: { entry: [] } };
    }
    if (!cachedEntries.dreamlo.leaderboard) {
        cachedEntries.dreamlo.leaderboard = { entry: [] };
    }
    
    let entries = cachedEntries.dreamlo.leaderboard.entry;
    if (!Array.isArray(entries)) {
        entries = entries ? [entries] : [];
    }
    
    // Check if player already has an entry in cached global list
    const existingIdx = entries.findIndex(e => {
        let decName = decodeURIComponent(e.name || '');
        if (decName.endsWith(' 👾')) decName = decName.substring(0, decName.length - 2);
        else if (decName.endsWith(' _ARCADE')) decName = decName.substring(0, decName.length - 8);
        return decName === playerName;
    });
    
    if (existingIdx !== -1) {
        // Only update if the new score is better!
        const existingEntry = entries[existingIdx];
        const existingDecText = decodeURIComponent(existingEntry.text || '');
        const parts = existingDecText.split('|');
        if (parts.length >= 2) {
            const existingScore = parseInt(parts[1]);
            if (isArcade) {
                if (scoreValue > existingScore) {
                    entries[existingIdx] = entryData;
                }
            } else {
                if (scoreValue < existingScore) {
                    entries[existingIdx] = entryData;
                }
            }
        }
    } else {
        entries.push(entryData);
    }
    
    cachedEntries.dreamlo.leaderboard.entry = entries;
    setCachedEntriesForCourse(courseId, cachedEntries);
}

export function saveHighScore(courseIdentifier, scoreValue, parValue, totalDist, optimalDist, playerName = state.currentPlayerName) {
    if (!state.courseHighScores[courseIdentifier]) {
        state.courseHighScores[courseIdentifier] = [];
    }
    const date = new Date().toLocaleDateString();
    const timestamp = Date.now();
    state.lastRunTimestamp = timestamp; // Save exact completed game timestamp
    let newScoreEntry;

    if (courseIdentifier === ARCADE_MODE_ID) {
        newScoreEntry = { 
            holesCompleted: scoreValue, 
            totalDistance: totalDist, 
            optimalDistance: optimalDist, 
            date, 
            playerName: playerName,
            timestamp: timestamp
        };
        console.log(`Arcade High Score Entry for ${playerName}: Holes ${newScoreEntry.holesCompleted}, Dist ${totalDist.toFixed(0)}`);
    } else { 
        newScoreEntry = { 
            score: scoreValue, 
            par: parValue, 
            totalDistance: totalDist, 
            optimalDistance: optimalDist, 
            date, 
            playerName: playerName,
            timestamp: timestamp
        };
        console.log(`Classic/Custom High Score Entry for ${playerName} on ${courseIdentifier}: Score ${newScoreEntry.score}, Par ${parValue}`);
    }

    state.courseHighScores[courseIdentifier].push(newScoreEntry);

    if (courseIdentifier === ARCADE_MODE_ID) {
        state.courseHighScores[courseIdentifier].sort((a, b) => {
            if (b.holesCompleted !== a.holesCompleted) return b.holesCompleted - a.holesCompleted; 
            if (a.totalDistance !== b.totalDistance) return a.totalDistance - b.totalDistance; 
            return getTime(b) - getTime(a); 
        });
    } else { 
        state.courseHighScores[courseIdentifier].sort((a, b) => {
            if (a.score !== b.score) return a.score - b.score; 
            return getTime(b) - getTime(a); 
        });
    }
    
    // Retain up to 500 records for stacking comparisons
    state.courseHighScores[courseIdentifier] = state.courseHighScores[courseIdentifier].slice(0, 500);
    localStorage.setItem('spaceGolfHighScores', JSON.stringify(state.courseHighScores));
    console.log(`High score saved for ${courseIdentifier}. Entries: ${state.courseHighScores[courseIdentifier].length}`);
    
    // Manually inject the new high score directly into the cached global entries array
    addHighScoreToCachedGlobalEntries(courseIdentifier, scoreValue, parValue, totalDist, optimalDist, playerName);

    // Automatically publish score to our free global Dreamlo database!
    uploadHighScoreToGlobal(courseIdentifier, scoreValue, parValue, totalDist, optimalDist, playerName);
}

export async function fetchDreamlo(endpoint) {
    // Helper to validate if the raw text is a valid Dreamlo response
    function validateDreamloResponse(text, isWrite) {
        if (!text) return false;
        const trimmed = text.trim();
        if (isWrite) {
            // Write endpoint natively returns exactly "OK"
            return trimmed === "OK";
        } else {
            // Read endpoint returns a JSON string containing the "dreamlo" key
            try {
                const parsed = JSON.parse(trimmed);
                return parsed && parsed.dreamlo !== undefined;
            } catch (e) {
                return false;
            }
        }
    }

    const isWrite = endpoint.includes('/add/');
    const proxyUrl = `/api/dreamlo${endpoint}`;

    try {
        const response = await fetch(proxyUrl);
        if (!response.ok) {
            throw new Error(`Proxy response status not ok: ${response.status}`);
        }
        
        // Read the body as text to validate it robustly
        const text = await response.text();
        if (!validateDreamloResponse(text, isWrite)) {
            throw new Error("Proxy returned invalid Dreamlo response body (likely SPA fallback).");
        }
        
        // Re-create a response object since the body has already been read
        return new Response(text, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
        });
    } catch (err) {
        console.warn(`⚠️ Netlify/Vite proxy failed for ${endpoint}. Falling back to CodeTabs CORS proxy... Error:`, err);
        
        // 2. Fall back to CodeTabs CORS proxy using http://dreamlo.com/lb
        const directUrl = `http://dreamlo.com/lb${endpoint}`;
        const fallbackUrl = `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(directUrl)}`;
        
        const response = await fetch(fallbackUrl);
        if (!response.ok) {
            throw new Error(`CodeTabs fallback failed with status: ${response.status}`);
        }
        
        const text = await response.text();
        if (!validateDreamloResponse(text, isWrite)) {
            throw new Error("CodeTabs fallback returned invalid Dreamlo response body.");
        }
        
        return new Response(text, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
        });
    }
}


export function uploadHighScoreToGlobal(courseId, scoreValue, parValue, totalDist, optimalDist, playerName) {
    const isArcade = courseId === ARCADE_MODE_ID || courseId === 'Arcade';
    // Append unique suffix for Arcade Mode to isolate entries in Dreamlo single-entry-per-name database
    const globalName = isArcade ? `${playerName} _ARCADE` : playerName;

    // For Classic, since Dreamlo is configured as Descending, we send (10000 - scoreValue) to Dreamlo so a lower strokes score is treated as "higher/better".
    // For Arcade, higher holes completed is better, so we send the scoreValue (holes count) directly.
    const dreamloScore = isArcade ? scoreValue : Math.max(0, 10000 - scoreValue);

    const tDist = Number(totalDist) || 0;
    const oDist = Number(optimalDist) || 0;
    const textData = [courseId, scoreValue, parValue, tDist.toFixed(0), oDist.toFixed(0), Date.now()].join('|');
    
    // Resolve write/private key based on course mode
    const keys = getDreamloKeys(courseId);
    const endpoint = `/${keys.private}/add/${encodeURIComponent(globalName)}/${dreamloScore}/0/${encodeURIComponent(textData)}`;
    
    fetchDreamlo(endpoint)
        .then(res => {
            console.log("Uploaded highscore globally to Dreamlo successfully.");
        })
        .catch(err => console.error("Error uploading highscore to Dreamlo:", err));
}

export let activeHighScoreCourseId = null;
export let activeHighScoreSyncType = 'local';

// Keyed by game mode key: { 'Classic': data, 'Arcade': data }
export let cachedGlobalEntries = {};

export function getCachedEntriesForCourse(courseId) {
    const isArcade = courseId === ARCADE_MODE_ID || courseId === 'Arcade';
    const key = isArcade ? 'Arcade' : 'Classic';
    return cachedGlobalEntries[key] || null;
}

export function setCachedEntriesForCourse(courseId, data) {
    const isArcade = courseId === ARCADE_MODE_ID || courseId === 'Arcade';
    const key = isArcade ? 'Arcade' : 'Classic';
    cachedGlobalEntries[key] = data;
}

// Paginated scoreboard state variables
export let currentScoreboardScoresList = [];
export let currentScoreboardDisplayLimit = 30;
export let currentScoreboardCourseId = null;
export let currentScoreboardListElement = null;

export function invalidateLeaderboardCache() {
    cachedGlobalEntries = {};
}

export function preloadGlobalLeaderboardData(courseId = null) {
    if (courseId) {
        const cache = getCachedEntriesForCourse(courseId);
        if (cache) return;
        const keys = getDreamloKeys(courseId);
        const endpoint = `/${keys.public}/json`;
        console.log(`⚡ Preloading global leaderboard data for ${courseId} in background...`);
        fetchDreamlo(endpoint)
            .then(res => res.json())
            .then(data => {
                setCachedEntriesForCourse(courseId, data);
                console.log(`⚡ Preloaded global leaderboard data for ${courseId} successfully cached!`);
            })
            .catch(err => {
                console.warn(`Background global leaderboard preload for ${courseId} failed:`, err);
            });
    } else {
        // Automatically preload both Classic and Arcade modes on startup
        preloadGlobalLeaderboardData('Classic');
        preloadGlobalLeaderboardData(ARCADE_MODE_ID);
    }
}

export function renderCurrentScoreboardBatch() {
    if (!currentScoreboardListElement || !currentScoreboardScoresList) return;
    
    const batch = currentScoreboardScoresList.slice(0, currentScoreboardDisplayLimit);
    renderScoreboardList(currentScoreboardCourseId, batch, currentScoreboardListElement);
    
    if (currentScoreboardDisplayLimit < currentScoreboardScoresList.length) {
        // Create an elegant clickable "VIEW MORE" button at the bottom of the table
        const loadMoreBtn = document.createElement('button');
        loadMoreBtn.className = 'w-full py-2.5 mt-2 bg-slate-900 border border-slate-700 hover:bg-slate-800 text-cyan-400 font-orbitron font-extrabold text-[10px] rounded-lg shadow-md transition-all tracking-wider uppercase cursor-pointer flex items-center justify-center gap-1.5 active:scale-95';
        loadMoreBtn.innerHTML = `<span>⬇️ VIEW MORE (${currentScoreboardScoresList.length - currentScoreboardDisplayLimit} left)</span>`;
        loadMoreBtn.addEventListener('click', () => {
            currentScoreboardDisplayLimit += 30; // Load 30 more
            renderCurrentScoreboardBatch();
        });
        currentScoreboardListElement.appendChild(loadMoreBtn);
    }
}

export function getPersonalBestComparison(courseId, currentScore, currentPar, currentRunTimestamp) {
    const scores = state.courseHighScores[courseId] || [];
    const pastScores = scores.filter(entry => entry.timestamp !== currentRunTimestamp);
    if (pastScores.length === 0) {
        return "First recorded run in this Sector! 🌌";
    }

    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    const getBest = (list) => {
        if (list.length === 0) return null;
        if (courseId === ARCADE_MODE_ID) {
            return list.reduce((best, entry) => {
                if (entry.holesCompleted > best.holesCompleted) return entry;
                if (entry.holesCompleted === best.holesCompleted && entry.totalDistance < best.totalDistance) return entry;
                return best;
            }, list[0]);
        } else {
            return list.reduce((best, entry) => entry.score < best.score ? entry : best, list[0]);
        }
    };

    const allTimeBest = getBest(pastScores);
    
    const monthlyList = pastScores.filter(e => (e.timestamp || Date.parse(e.date) || now) >= now - 30 * oneDay);
    const monthlyBest = getBest(monthlyList);

    const weeklyList = pastScores.filter(e => (e.timestamp || Date.parse(e.date) || now) >= now - 7 * oneDay);
    const weeklyBest = getBest(weeklyList);

    const dailyList = pastScores.filter(e => (e.timestamp || Date.parse(e.date) || now) >= now - 1 * oneDay);
    const dailyBest = getBest(dailyList);

    if (courseId === ARCADE_MODE_ID) {
        const checkBetter = (currScore, bestEntry) => {
            if (!bestEntry) return true;
            return currScore > bestEntry.holesCompleted;
        };

        if (checkBetter(currentScore, allTimeBest)) return "🔥 NEW PERSONAL RECORD!";
        if (monthlyBest && checkBetter(currentScore, monthlyBest)) return "⚡ NEW MONTHLY BEST!";
        if (weeklyBest && checkBetter(currentScore, weeklyBest)) return "✨ NEW WEEKLY BEST!";
        if (dailyBest && checkBetter(currentScore, dailyBest)) return "🌟 NEW DAILY BEST!";

        if (allTimeBest) {
            const diff = allTimeBest.holesCompleted - currentScore;
            if (diff === 0) return "Matched your Personal Best! 🛸";
            return `${diff} hole${diff > 1 ? 's' : ''} off Your Best`;
        }
    } else {
        const checkBetter = (currScore, bestEntry) => {
            if (!bestEntry) return true;
            return currScore < bestEntry.score;
        };

        if (checkBetter(currentScore, allTimeBest)) return "🔥 NEW PERSONAL RECORD!";
        if (monthlyBest && checkBetter(currentScore, monthlyBest)) return "⚡ NEW MONTHLY BEST!";
        if (weeklyBest && checkBetter(currentScore, weeklyBest)) return "✨ NEW WEEKLY BEST!";
        if (dailyBest && checkBetter(currentScore, dailyBest)) return "🌟 NEW DAILY BEST!";

        if (allTimeBest) {
            const diff = currentScore - allTimeBest.score;
            if (diff === 0) return "Matched your Personal Best! 🛸";
            return `${diff} shot${diff > 1 ? 's' : ''} off Your Best`;
        }
    }

    return "Keep pushin' the limits! 🚀";
}

export function getGlobalRankingForScore(courseId, score, totalDist) {
    const cachedData = getCachedEntriesForCourse(courseId);
    if (!cachedData) return null;

    let rawEntries = [];
    if (cachedData.dreamlo && cachedData.dreamlo.leaderboard && cachedData.dreamlo.leaderboard.entry) {
        const entryData = cachedData.dreamlo.leaderboard.entry;
        rawEntries = Array.isArray(entryData) ? entryData : [entryData];
    }

    let globalScores = [];
    rawEntries.forEach(e => {
        let decodedName = decodeURIComponent(e.name);
        if (decodedName.endsWith(' 👾')) {
            decodedName = decodedName.substring(0, decodedName.length - 2);
        }

        const decodedText = decodeURIComponent(e.text || '');
        const parts = decodedText.split('|');
        
        if (parts.length >= 5) {
            const entryMode = parts[0];
            const entryScore = parseInt(parts[1]);
            const entryTotalDist = parseFloat(parts[3]);
            
            const isArcadeMatch = (entryMode === 'Arcade Mode' || entryMode === 'Arcade') && (courseId === 'Arcade Mode' || courseId === 'Arcade');
            const isClassicMatch = (entryMode === 'Random Course' || entryMode === 'classic') && (courseId === 'Random Course' || courseId === 'classic');

            if (entryMode === courseId || isArcadeMatch || isClassicMatch) {
                globalScores.push({
                    playerName: decodedName,
                    score: entryScore,
                    totalDistance: entryTotalDist
                });
            }
        }
    });

    // Deduplicate by playerName, keeping only their BEST score
    const bestScoresMap = new Map();
    globalScores.forEach(s => {
        const existing = bestScoresMap.get(s.playerName);
        if (!existing) {
            bestScoresMap.set(s.playerName, s);
        } else {
            if (courseId === ARCADE_MODE_ID) {
                if (s.score > existing.score) {
                    bestScoresMap.set(s.playerName, s);
                } else if (s.score === existing.score && s.totalDistance < existing.totalDistance) {
                    bestScoresMap.set(s.playerName, s);
                }
            } else {
                if (s.score < existing.score) {
                    bestScoresMap.set(s.playerName, s);
                } else if (s.score === existing.score && s.totalDistance < existing.totalDistance) {
                    bestScoresMap.set(s.playerName, s);
                }
            }
        }
    });

    // Add our current run if not already present in comparison
    const currentPilot = state.currentPlayerName || 'Player';
    const existingPilotScore = bestScoresMap.get(currentPilot);
    
    let shouldAdd = true;
    if (existingPilotScore) {
        if (courseId === ARCADE_MODE_ID) {
            if (score <= existingPilotScore.score) shouldAdd = false;
        } else {
            if (score >= existingPilotScore.score) shouldAdd = false;
        }
    }
    
    if (shouldAdd) {
        bestScoresMap.set(currentPilot, {
            playerName: currentPilot,
            score: score,
            totalDistance: totalDist
        });
    }

    let sortedScores = Array.from(bestScoresMap.values());
    if (courseId === ARCADE_MODE_ID) {
        sortedScores.sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return a.totalDistance - b.totalDistance;
        });
    } else {
        sortedScores.sort((a, b) => {
            if (a.score !== b.score) return a.score - b.score;
            return a.totalDistance - b.totalDistance;
        });
    }

    const rankIndex = sortedScores.findIndex(s => s.playerName === currentPilot && s.score === score);
    if (rankIndex !== -1) {
        return {
            rank: rankIndex + 1,
            total: sortedScores.length
        };
    }

    return null;
}

export function displayHighScores(courseId, filterType = 'all', syncType = activeHighScoreSyncType) {
    activeHighScoreCourseId = courseId;
    activeHighScoreSyncType = syncType;
    
    // Set dynamic, smaller responsive leaderboard title based on current filters
    const modeName = courseId === ARCADE_MODE_ID ? "Arcade" : "Classic";
    const filterName = filterType === 'all' ? "All-Time" : (filterType.charAt(0).toUpperCase() + filterType.slice(1));
    const syncName = syncType === 'global' ? "World Rankings" : "Personal Bests";
    
    const titleEl = document.getElementById('highScoreDisplayTitle');
    if (titleEl) {
        titleEl.textContent = `${filterName} ${modeName} ${syncName}`;
    }
    
    const listElement = document.getElementById('highScoreDisplayList');
    if (listElement) {
        listElement.innerHTML = '';
        listElement.scrollTop = 0;
    } 
    
    if (syncType === 'global') {
        const processDataAndRender = (data) => {
            if (activeHighScoreCourseId !== courseId || activeHighScoreSyncType !== 'global') return; // protect against race conditions
            listElement.innerHTML = '';
            
            let rawEntries = [];
            if (data && data.dreamlo && data.dreamlo.leaderboard && data.dreamlo.leaderboard.entry) {
                const entryData = data.dreamlo.leaderboard.entry;
                rawEntries = Array.isArray(entryData) ? entryData : [entryData];
            }
            
            // Parse global entries
            let globalScores = [];
            rawEntries.forEach(e => {
                let decodedName = decodeURIComponent(e.name);
                // Strip the Arcade isolation suffix if present
                if (decodedName.endsWith(' 👾')) {
                    decodedName = decodedName.substring(0, decodedName.length - 2);
                } else if (decodedName.endsWith(' _ARCADE')) {
                    decodedName = decodedName.substring(0, decodedName.length - 8);
                }

                const decodedText = decodeURIComponent(e.text || '');
                const parts = decodedText.split('|');
                
                if (parts.length >= 5) {
                    const entryMode = parts[0];
                    const entryScore = parseInt(parts[1]);
                    const entryPar = parseInt(parts[2]);
                    const entryTotalDist = parseFloat(parts[3]);
                    const entryOptimalDist = parseFloat(parts[4]);
                    const entryTimestamp = parts[5] ? parseInt(parts[5]) : (parseDreamloDate(e.date) || Date.now());
                    
                    const isArcadeMatch = (entryMode === 'Arcade Mode' || entryMode === 'Arcade') && (courseId === 'Arcade Mode' || courseId === 'Arcade');
                    const isClassicMatch = (entryMode === 'Random Course' || entryMode === 'classic') && (courseId === 'Random Course' || courseId === 'classic');

                    if (entryMode === courseId || isArcadeMatch || isClassicMatch) {
                        globalScores.push({
                            playerName: decodedName,
                            score: entryScore,
                            holesCompleted: entryScore, // for arcade
                            par: entryPar,
                            totalDistance: entryTotalDist,
                            optimalDistance: entryOptimalDist,
                            date: e.date,
                            timestamp: entryTimestamp
                        });
                    }
                }
            });
            
            // Skip deduplication to show all score attempts from all players
            let processedScores = [...globalScores];
            
            // Sort processed scores authoritatively
            if (courseId === ARCADE_MODE_ID) {
                processedScores.sort((a, b) => {
                    if (b.holesCompleted !== a.holesCompleted) return b.holesCompleted - a.holesCompleted; 
                    if (a.totalDistance !== b.totalDistance) return a.totalDistance - b.totalDistance; 
                    return b.timestamp - a.timestamp;
                });
            } else {
                processedScores.sort((a, b) => {
                    if (a.score !== b.score) return a.score - b.score; 
                    if (a.totalDistance !== b.totalDistance) return a.totalDistance - b.totalDistance; 
                    return b.timestamp - a.timestamp;
                });
            }
            
            // Apply time filter
            const now = Date.now();
            let cutoff = 0;
            if (filterType === 'daily') {
                const today = new Date();
                today.setHours(0, 0, 0, 0); // start of today, local time
                cutoff = today.getTime();
            } else if (filterType === 'weekly') {
                cutoff = now - 7 * 24 * 60 * 60 * 1000;
            } else if (filterType === 'monthly') {
                cutoff = now - 30 * 24 * 60 * 60 * 1000;
            }
            
            if (cutoff > 0) {
                processedScores = processedScores.filter(s => s.timestamp >= cutoff);
            }
            
            // AUTOMAGICAL INJECTION: Inject player's best local score for this period!
            const localScores = state.courseHighScores[courseId] || [];
            const localScoresInPeriod = localScores.filter(s => {
                if (cutoff === 0) return true;
                const t = s.timestamp || (s.date ? Date.parse(s.date) : 0);
                return t >= cutoff;
            });
            
            if (localScoresInPeriod.length > 0) {
                // Since state.courseHighScores is pre-sorted inside saveHighScore,
                // the first entry in localScoresInPeriod is guaranteed to be their local best for this period!
                const localBest = localScoresInPeriod[0];
                const myName = state.currentPlayerName || 'Player';
                
                const localScoreVal = courseId === ARCADE_MODE_ID ? (localBest.holesCompleted || 0) : (localBest.score || 0);
                const localParVal = localBest.par || 0;
                const localTotalDist = localBest.totalDistance || 0;
                const localOptimalDist = localBest.optimalDistance || 0;
                const localTimestamp = localBest.timestamp || Date.now();
                
                // Check if they are already on the global board for this period
                const existingMyGlobalIdx = processedScores.findIndex(s => {
                    let decName = s.playerName || '';
                    return decName === myName;
                });
                
                let shouldInjectLocal = false;
                if (existingMyGlobalIdx === -1) {
                    shouldInjectLocal = true;
                } else {
                    // Compare their local best in this period with their parsed global entry in this period.
                    // If the local best is better, we replace the global entry to show their true best achievement!
                    const globalEntry = processedScores[existingMyGlobalIdx];
                    if (courseId === ARCADE_MODE_ID) {
                        if (localScoreVal > globalEntry.score) {
                            processedScores.splice(existingMyGlobalIdx, 1); // remove inferior entry
                            shouldInjectLocal = true;
                        }
                    } else {
                        if (localScoreVal < globalEntry.score) {
                            processedScores.splice(existingMyGlobalIdx, 1); // remove inferior entry
                            shouldInjectLocal = true;
                        }
                    }
                }
                
                if (shouldInjectLocal) {
                    processedScores.push({
                        playerName: myName,
                        score: localScoreVal,
                        holesCompleted: localScoreVal, // for arcade
                        par: localParVal,
                        totalDistance: localTotalDist,
                        optimalDistance: localOptimalDist,
                        date: new Date(localTimestamp).toLocaleDateString(),
                        timestamp: localTimestamp,
                        isLocalPBInjection: true // Custom flag to highlight or badge the local run
                    });
                    
                    // Re-sort processed scores with the injected local best included!
                    if (courseId === ARCADE_MODE_ID) {
                        processedScores.sort((a, b) => {
                            if (b.holesCompleted !== a.holesCompleted) return b.holesCompleted - a.holesCompleted; 
                            if (a.totalDistance !== b.totalDistance) return a.totalDistance - b.totalDistance; 
                            return b.timestamp - a.timestamp;
                        });
                    } else {
                        processedScores.sort((a, b) => {
                            if (a.score !== b.score) return a.score - b.score; 
                            if (a.totalDistance !== b.totalDistance) return a.totalDistance - b.totalDistance; 
                            return b.timestamp - a.timestamp;
                        });
                    }
                }
            }
            
            // Set paginated scoreboard variables
            currentScoreboardScoresList = processedScores;
            currentScoreboardDisplayLimit = 30;
            currentScoreboardCourseId = courseId;
            currentScoreboardListElement = listElement;
            
            // Hook dynamic infinite scroll listener
            if (listElement && !listElement.dataset.scrollBound) {
                listElement.dataset.scrollBound = 'true';
                listElement.addEventListener('scroll', () => {
                    const threshold = 45;
                    if (listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight < threshold) {
                        if (currentScoreboardScoresList && currentScoreboardDisplayLimit < currentScoreboardScoresList.length) {
                            currentScoreboardDisplayLimit += 30; // Load 30 more
                            renderCurrentScoreboardBatch();
                            console.log(`Leaderboard paginated to ${currentScoreboardDisplayLimit} entries.`);
                        }
                    }
                });
            }
            
            renderCurrentScoreboardBatch();
        };

        const cachedData = getCachedEntriesForCourse(courseId);
        const keys = getDreamloKeys(courseId);

        if (cachedData) {
            processDataAndRender(cachedData);
            
            // Stealth background sync to keep things synchronized silently without showing a loading spinner!
            fetchDreamlo(`/${keys.public}/json`)
                .then(res => res.json())
                .then(data => {
                    setCachedEntriesForCourse(courseId, data); // Update cache silently
                    // Repaint the active display if syncType is still 'global' and course matches
                    if (activeHighScoreCourseId === courseId && activeHighScoreSyncType === 'global') {
                        processDataAndRender(data);
                    }
                })
                .catch(err => console.warn("Silent background leaderboard sync failed:", err));
        } else {
            // Show loading indicator
            const loadingEl = document.createElement('div');
            loadingEl.className = 'text-center py-8 font-orbitron font-bold text-xs tracking-widest text-cyan-400 animate-pulse';
            loadingEl.innerHTML = '⚡ UPDATING GLOBAL SECTOR...';
            listElement.appendChild(loadingEl);
            
            fetchDreamlo(`/${keys.public}/json`)
                .then(res => res.json())
                .then(data => {
                    setCachedEntriesForCourse(courseId, data); // Cache global data dynamically
                    processDataAndRender(data);
                })
                .catch(err => {
                    console.error("Global leaderboard fetch failed:", err);
                    listElement.innerHTML = '<p class="text-gray-400 text-center py-4">⚠️ FAILED TO CONNECT TO GLOBAL SECTOR</p>';
                });
        }
    } else {
        // Local Cabinet Scores
        let scores = [...(state.courseHighScores[courseId] || [])];
        
        // Sort local cabinet scores first robustly!
        if (courseId === ARCADE_MODE_ID) {
            scores.sort((a, b) => {
                if (b.holesCompleted !== a.holesCompleted) return b.holesCompleted - a.holesCompleted;
                if (a.totalDistance !== b.totalDistance) return a.totalDistance - b.totalDistance;
                return getTime(b) - getTime(a);
            });
        } else {
            scores.sort((a, b) => {
                if (a.score !== b.score) return a.score - b.score;
                if (a.totalDistance !== b.totalDistance) return a.totalDistance - b.totalDistance;
                return getTime(b) - getTime(a);
            });
        }
        
        // Apply time filter
        const now = Date.now();
        let cutoff = 0;
        if (filterType === 'daily') {
            const today = new Date();
            today.setHours(0, 0, 0, 0); // start of today, local time
            cutoff = today.getTime();
        } else if (filterType === 'weekly') {
            cutoff = now - 7 * 24 * 60 * 60 * 1000;
        } else if (filterType === 'monthly') {
            cutoff = now - 30 * 24 * 60 * 60 * 1000;
        }
        
        if (cutoff > 0) {
            scores = scores.filter(entry => {
                const entryTime = entry.timestamp !== undefined ? entry.timestamp : (Date.parse(entry.date) || now);
                return entryTime >= cutoff;
            });
        }
        
        // Set paginated scoreboard variables
        currentScoreboardScoresList = scores;
        currentScoreboardDisplayLimit = 30;
        currentScoreboardCourseId = courseId;
        currentScoreboardListElement = listElement;
        
        // Hook dynamic infinite scroll listener
        if (listElement && !listElement.dataset.scrollBound) {
            listElement.dataset.scrollBound = 'true';
            listElement.addEventListener('scroll', () => {
                const threshold = 45;
                if (listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight < threshold) {
                    if (currentScoreboardScoresList && currentScoreboardDisplayLimit < currentScoreboardScoresList.length) {
                        currentScoreboardDisplayLimit += 30; // Load 30 more
                        renderCurrentScoreboardBatch();
                        console.log(`Leaderboard paginated to ${currentScoreboardDisplayLimit} entries.`);
                    }
                }
            });
        }
        
        renderCurrentScoreboardBatch();
    }
}

export function parseDreamloDate(dateStr) {
    if (!dateStr) return null;
    try {
        const parts = dateStr.trim().split(' ');
        if (parts.length >= 2) {
            const dateParts = parts[0].split('/');
            const timeParts = parts[1].split(':');
            if (dateParts.length === 3 && timeParts.length >= 2) {
                const month = parseInt(dateParts[0]) - 1;
                const day = parseInt(dateParts[1]);
                const year = parseInt(dateParts[2]);
                let hour = parseInt(timeParts[0]);
                const minute = parseInt(timeParts[1]);
                const second = timeParts[2] ? parseInt(timeParts[2]) : 0;
                
                if (parts[2] && parts[2].toUpperCase() === 'PM' && hour < 12) hour += 12;
                if (parts[2] && parts[2].toUpperCase() === 'AM' && hour === 12) hour = 0;
                
                const utcTime = Date.UTC(year, month, day, hour, minute, second);
                if (!isNaN(utcTime)) {
                    return utcTime;
                }
            }
        }
    } catch (e) {
        console.warn("Failed to manually parse Dreamlo date:", dateStr, e);
    }
    const fallback = Date.parse(dateStr);
    return isNaN(fallback) ? null : fallback;
}

export function renderScoreboardList(courseId, scores, listElement) {
    listElement.innerHTML = '';
    
    // 1. Category Header Row
    const headerRow = document.createElement('div');
    headerRow.className = 'hs-header-row';
    headerRow.innerHTML = `
        <div class="hs-col-rank">RK</div>
        <div class="hs-col-pilot">PLAYER</div>
        <div class="hs-col-score">${courseId === ARCADE_MODE_ID ? 'HOLES' : 'SCORE (PAR)'}</div>
        <div class="hs-col-acc">EFF</div>
        <div class="hs-col-date">DATE</div>
    `;
    listElement.appendChild(headerRow);
    
    if (scores.length === 0) {
        const emptyEl = document.createElement('div');
        emptyEl.className = 'text-center py-10 font-orbitron font-bold text-xs tracking-wider text-cyan-400/80 animate-pulse';
        if (activeHighScoreSyncType === 'global') {
            emptyEl.innerHTML = '⚡ NO GLOBAL RECORD ENTRIES FOUND FOR THIS PERIOD ⚡';
        } else {
            emptyEl.innerHTML = '⚡ NO LOCAL CABINET ATTEMPTS RECORDED ⚡';
        }
        listElement.appendChild(emptyEl);
        return;
    }
    
    // 2. Score Rows
    scores.forEach((entry, index) => {
        const rank = index + 1;
        const isLatestRun = entry.timestamp && state.lastRunTimestamp && (entry.timestamp === state.lastRunTimestamp || Math.abs(entry.timestamp - state.lastRunTimestamp) < 100);
        const isMyRun = entry.isLocalPBInjection || (entry.playerName && state.currentPlayerName && (entry.playerName === state.currentPlayerName || entry.playerName.trim() === state.currentPlayerName.trim()));
        const row = document.createElement('div');
        row.className = `hs-row ${rank === 1 ? 'hs-row-gold' : ''} ${isLatestRun || isMyRun ? 'hs-row-current-run' : ''}`;
        
        // Rank Badge
        let rankBadgeContent = rank;
        let rankBadgeClass = '';
        if (rank === 1) { rankBadgeContent = '👑'; rankBadgeClass = 'hs-rank-gold'; }
        else if (rank === 2) { rankBadgeContent = '🥈'; rankBadgeClass = 'hs-rank-silver'; }
        else if (rank === 3) { rankBadgeContent = '🥉'; rankBadgeClass = 'hs-rank-bronze'; }
        else { rankBadgeContent = rank < 10 ? '0' + rank : rank; }
        
        // Score/Holes pill
        let scorePill = '';
        if (courseId === ARCADE_MODE_ID) {
            scorePill = `<span class="hs-pill hs-pill-purple">${entry.holesCompleted} HOLES</span>`;
        } else {
            const relPar = entry.score - entry.par;
            let parText = '';
            let pillClass = '';
            if (relPar < 0) { parText = `${relPar} UNDER`; pillClass = 'hs-pill-green'; }
            else if (relPar === 0) { parText = 'PAR'; pillClass = 'hs-pill-green'; }
            else { parText = `+${relPar} OVER`; pillClass = 'hs-pill-red'; }
            scorePill = `<span class="hs-pill ${pillClass}">${entry.score} (${parText})</span>`;
        }
        
        // Flight Efficiency Rating
        let accPill = '--';
        if (entry.totalDistance && entry.optimalDistance) {
            const efficiency = Math.min(100, Math.round((entry.optimalDistance / entry.totalDistance) * 100));
            accPill = `<span class="hs-pill hs-pill-cyan">${efficiency}%</span>`;
        }
        
        // Format Date
        let formattedDate = entry.date;
        try {
            const dateObj = new Date(entry.timestamp || parseDreamloDate(entry.date) || Date.now());
            const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            formattedDate = `${months[dateObj.getMonth()]} ${dateObj.getDate()}`;
        } catch(e) {}
        
        // Premium Badge Logic
        let badgeSpan = '';
        if (isLatestRun) {
            badgeSpan = ' <span class="hs-latest-badge">YOUR RUN</span>';
        } else if (entry.isLocalPBInjection) {
            badgeSpan = ' <span class="hs-latest-badge" style="background: rgba(234, 179, 8, 0.15); color: #EAB308; border-color: rgba(234, 179, 8, 0.3);">YOUR BEST (LOCAL)</span>';
        } else if (isMyRun) {
            badgeSpan = ' <span class="hs-latest-badge">YOU</span>';
        }
        
        row.innerHTML = `
            <div class="hs-col-rank">
                <div class="hs-rank-badge ${rankBadgeClass}">${rankBadgeContent}</div>
            </div>
            <div class="hs-col-pilot hs-pilot-name">${entry.playerName || 'Player'}${badgeSpan}</div>
            <div class="hs-col-score">${scorePill}</div>
            <div class="hs-col-acc">${accPill}</div>
            <div class="hs-col-date">${formattedDate}</div>
        `;
        listElement.appendChild(row);
    });
}

export function populateHighScoreCourseList() {
    const container = document.getElementById('highScoreCourseListContainer');
    container.innerHTML = '';
    
    const displayableCourseIds = new Set();
    if (state.courseHighScores[RANDOM_COURSE_ID] && state.courseHighScores[RANDOM_COURSE_ID].length > 0) {
        displayableCourseIds.add(RANDOM_COURSE_ID);
    }
    if (state.courseHighScores[ARCADE_MODE_ID] && state.courseHighScores[ARCADE_MODE_ID].length > 0) {
        displayableCourseIds.add(ARCADE_MODE_ID);
    }
    state.savedCourses.forEach(course => {
        if (state.courseHighScores[course.name] && state.courseHighScores[course.name].length > 0) {
            displayableCourseIds.add(course.name);
        }
    });
    Object.keys(state.courseHighScores).forEach(id => {
        if (state.courseHighScores[id] && state.courseHighScores[id].length > 0) {
             displayableCourseIds.add(id);
        }
    });

    if (displayableCourseIds.size === 0) {
        container.innerHTML = '<p class="text-gray-400">No high scores recorded yet for any course.</p>';
        return;
    }
    
    const order = [ARCADE_MODE_ID, RANDOM_COURSE_ID]; 

    order.forEach(courseId => {
        if (displayableCourseIds.has(courseId)) {
            const button = document.createElement('button');
            button.className = 'list-item-button';
            button.textContent = `${courseId === RANDOM_COURSE_ID ? "Classic 9 Hole" : courseId} High Scores`;
            if (courseId === ARCADE_MODE_ID) button.classList.add('!bg-purple-600', 'hover:!bg-purple-700');
            button.onclick = () => {
                document.getElementById('highScoreDisplayTitle').textContent = `High Scores: ${courseId === RANDOM_COURSE_ID ? "Classic 9 Hole" : courseId}`;
                displayHighScores(courseId);
                document.getElementById('highScoreCourseListModal')?.classList.remove('active');
                document.getElementById('highScoreDisplayModal').classList.add('active');
            };
            container.appendChild(button);
            displayableCourseIds.delete(courseId); 
        }
    });

    const sortedCustomCourseIds = Array.from(displayableCourseIds).sort((a,b) => a.localeCompare(b));

    sortedCustomCourseIds.forEach(courseId => {
        const button = document.createElement('button');
        button.className = 'list-item-button';
        button.textContent = `Course: ${courseId}`;
        button.onclick = () => {
            document.getElementById('highScoreDisplayTitle').textContent = `High Scores: ${courseId}`;
            displayHighScores(courseId);
            document.getElementById('highScoreCourseListModal')?.classList.remove('active');
            document.getElementById('highScoreDisplayModal').classList.add('active');
        };
        container.appendChild(button);
    });
}

export function syncLocalScoresToGlobal() {
    console.log("Synchronizing local high scores to global leaderboard...");
    let uploadCount = 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfToday = today.getTime();
    
    Object.keys(state.courseHighScores).forEach(courseId => {
        const scores = state.courseHighScores[courseId] || [];
        if (scores.length === 0) return;
        
        // Determine the targets to upload for this course
        const targetsToUpload = [];
        
        // 1. All-time personal best (index 0 because the list is pre-sorted)
        const bestScore = scores[0];
        targetsToUpload.push(bestScore);
        
        // 2. Best score from today (excluding all-time best to avoid double upload)
        const scoresToday = scores.filter(s => {
            if (s === bestScore) return false;
            const t = s.timestamp || (s.date ? Date.parse(s.date) : 0);
            return t >= startOfToday;
        });
        
        if (scoresToday.length > 0) {
            // First entry in scoresToday is guaranteed to be today's best run because scores is pre-sorted
            targetsToUpload.push(scoresToday[0]);
        }
        
        // Upload the selected targets
        targetsToUpload.forEach(s => {
            const scoreValue = courseId === ARCADE_MODE_ID ? (s.holesCompleted || 0) : (s.score || 0);
            const parValue = s.par || 0;
            const totalDist = s.totalDistance || 0;
            const optimalDist = s.optimalDistance || 0;
            const playerName = s.playerName || "Player";
            
            uploadHighScoreToGlobal(courseId, scoreValue, parValue, totalDist, optimalDist, playerName);
            uploadCount++;
        });
    });
    
    if (uploadCount > 0) {
        invalidateLeaderboardCache();
        console.log(`Successfully queued ${uploadCount} optimized local high scores (all-time & today's best) for global sync.`);
    }
}

export function triggerHoleBanner(holeIndex, par, isMultiplayer = false) {
    const bannerContainer = document.getElementById('largeLevelAnnouncement');
    const titleEl = document.getElementById('largeLevelTitle');
    const parEl = document.getElementById('largeLevelPar');

    if (!bannerContainer || !titleEl || !parEl) return;

    // Reset styles/opacity
    bannerContainer.style.opacity = '0';
    bannerContainer.style.transform = 'scale(0.85)';
    bannerContainer.style.transition = 'none';

    // Update text content
    if (holeIndex === 9 || (state.TOTAL_HOLES && holeIndex === state.TOTAL_HOLES)) {
        titleEl.textContent = "FINAL HOLE";
    } else {
        titleEl.textContent = `HOLE ${holeIndex}`;
    }

    if (isMultiplayer) {
        parEl.style.display = 'none';
        
        // Match classic cyan text color, thick white borders, and cyan glow
        titleEl.className = 'text-4xl sm:text-5xl md:text-7xl font-black font-orbitron text-cyan-400 tracking-widest text-shadow-cyan-glow uppercase text-center px-4';
        
        // Trigger reflow
        void bannerContainer.offsetWidth;
        
        // Fast transition-in (more like out of bounds fast but a hair slower: 380ms)
        bannerContainer.style.transition = 'opacity 380ms ease-out, transform 380ms cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        bannerContainer.style.opacity = '1';
        bannerContainer.style.transform = 'scale(1)';
        
        if (window.holeBannerTimeout) {
            clearTimeout(window.holeBannerTimeout);
        }
        // Stay for 600ms, then fade out quickly in 300ms
        window.holeBannerTimeout = setTimeout(() => {
            bannerContainer.style.transition = 'opacity 300ms ease-in, transform 300ms ease-in';
            bannerContainer.style.opacity = '0';
            bannerContainer.style.transform = 'scale(1.08)';
        }, 600);
    } else {
        parEl.style.display = 'block';
        parEl.textContent = `PAR ${par}`;
        
        // Standard cyan style
        titleEl.className = 'text-4xl sm:text-5xl md:text-7xl font-black font-orbitron text-cyan-400 tracking-widest text-shadow-cyan-glow uppercase text-center px-4';
        
        // Trigger reflow
        void bannerContainer.offsetWidth;
        
        // Standard transition-in
        bannerContainer.style.transition = 'opacity 300ms ease-out, transform 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        bannerContainer.style.opacity = '1';
        bannerContainer.style.transform = 'scale(1)';
        
        if (window.holeBannerTimeout) {
            clearTimeout(window.holeBannerTimeout);
        }
        window.holeBannerTimeout = setTimeout(() => {
            bannerContainer.style.transition = 'opacity 400ms ease-in, transform 400ms ease-in';
            bannerContainer.style.opacity = '0';
            bannerContainer.style.transform = 'scale(1.1)';
        }, 800);
    }
}

export function updateGameOverRankStamp(courseId, scoreValue, parValue, totalDistance) {
    const rankStamp = document.getElementById('rowLeaderboardRank');
    const rankValueEl = document.getElementById('txtLeaderboardRank');
    const rankCompEl = document.getElementById('txtLeaderboardComparison');
    if (!rankStamp || !rankValueEl || !rankCompEl) return;
    
    const rankLabelEl = rankStamp.querySelector('.scorecard-rank-stamp-label') || rankStamp.firstElementChild;
    
    function applyRankDisplay(rankInfo) {
        // Reset rank stamp modifier classes
        rankStamp.className = 'scorecard-rank-stamp cursor-pointer';
        
        // Fetch local comparison text
        const compText = getPersonalBestComparison(courseId, scoreValue, parValue, state.lastRunTimestamp);
        
        if (rankInfo) {
            rankLabelEl.textContent = "World Ranking";
            let rankText = `#${rankInfo.rank} / ${rankInfo.total}`;
            if (rankInfo.rank === 1) {
                rankText = "🏆 #1 World Record!";
                rankStamp.classList.add('rank-pb');
            } else if (rankInfo.rank > 1 && rankInfo.rank <= 10) {
                rankStamp.classList.add('rank-top10');
            } else {
                rankStamp.classList.add('rank-unranked');
            }
            rankValueEl.textContent = rankText;
            if (rankInfo.rank === 1) {
                rankCompEl.textContent = "🔥 NEW WORLD RECORD! 🚀";
            } else {
                rankCompEl.textContent = compText;
            }
        } else {
            // Fall back to Local Cabinet Ranking
            rankLabelEl.textContent = "Player Ranking";
            const scores = state.courseHighScores[courseId] || [];
            const rankIndex = scores.findIndex(entry => entry.timestamp === state.lastRunTimestamp);
            const totalScores = scores.length;
            
            let rankText = "Unranked";
            if (rankIndex === 0) {
                rankText = "🏆 #1 Personal Best!";
                rankStamp.classList.add('rank-pb');
            } else if (rankIndex > 0 && rankIndex < 10) {
                rankText = `#${rankIndex + 1} / ${totalScores}`;
                rankStamp.classList.add('rank-top10');
            } else {
                rankText = `#${rankIndex + 1} / ${totalScores}`;
                rankStamp.classList.add('rank-unranked');
            }
            rankValueEl.textContent = rankText;
            rankCompEl.textContent = compText;
        }
    }
    
    // 1. Initial display based on current cached entries
    const initialRank = getGlobalRankingForScore(courseId, scoreValue, totalDistance);
    applyRankDisplay(initialRank);
    
    // 2. Automagical background fetch of the latest live rankings to show actual live position!
    // We add a tiny delay of 800ms to allow the background post request to land first.
    setTimeout(() => {
        const keys = getDreamloKeys(courseId);
        fetchDreamlo(`/${keys.public}/json`)
            .then(res => res.json())
            .then(data => {
                setCachedEntriesForCourse(courseId, data); // Update cache
                
                // Recalculate ranking with the live data!
                const liveRank = getGlobalRankingForScore(courseId, scoreValue, totalDistance);
                
                // Apply a subtle neon highlight animation to show it updated automagically!
                rankStamp.classList.add('animate-pulse');
                applyRankDisplay(liveRank);
                setTimeout(() => rankStamp.classList.remove('animate-pulse'), 1500);
                
                console.log("Live rank stamp updated automagically with latest global data!");
            })
            .catch(err => {
                console.warn("Silent rank stamp global fetch failed, keeping current display:", err);
            });
    }, 800);
}

// --- Funny Cosmic Academy Slides & Listeners ---
const tutorialSlides = [
    {
        title: "🪐 DEFY GRAVITY",
        text: "Forget normal golf! You are shooting a sentient cartoon ball in deep space. <b>Drag backward</b> to stretch the slingshot band, aim, and release to fire! Gravity from nearby planets will bend your path. Tip: tangent takeoff shots give you massive gravity slingshots!"
    },
    {
        title: "🚀 CLASSIC TOUR (9-Hole Tournament)",
        text: "The traditional celestial golf tournament. Navigate through 9 planetary courses and sink the ball in the glowing cup under Par. Bouncing off planets is legal but wastes velocity. Don't drift into the infinite black void!"
    },
    {
        title: "👾 ARCADE SURVIVAL (Endless Chaos)",
        text: "The ultimate endless survival mode. The par limit resets every hole. Earn <b>Space Bucks 🪙</b> by completing holes efficiently, and spend them in the real-time Shop to buy overpowered gear like Lasers, Aegis Shields, or Homing Multi-Balls!"
    },
    {
        title: "🤢 COSMIC MEDICAL NOTICE",
        text: "Do not exceed safe angular velocity. Characters are highly sensitive to spin. Launching at extreme speeds will cause severe dizziness, spiral eye warps, and <b>violent green hurling</b> across space. You have been warned!"
    }
];

let currentTutorialSlide = 0;

export function setupTutorialModalListeners() {
    const tutorialModal = document.getElementById('tutorialModal');
    const tutorialModeBtn = document.getElementById('tutorialModeBtn');
    const closeTutorialBtn = document.getElementById('closeTutorialBtn');
    const tutPrevBtn = document.getElementById('tutPrevBtn');
    const tutNextBtn = document.getElementById('tutNextBtn');
    const tutSlideIndicator = document.getElementById('tutSlideIndicator');
    const slidesContainer = document.getElementById('tutorialSlidesContainer');
    
    if (!tutorialModal || !tutorialModeBtn) return;
    
    function updateSlide() {
        const slide = tutorialSlides[currentTutorialSlide];
        if (slidesContainer) {
            slidesContainer.innerHTML = `
                <h3 class="font-black text-cyan-300 font-orbitron mb-2" style="text-shadow: 1px 1px 0px #000;">${slide.title}</h3>
                <p class="text-xs text-gray-200 leading-relaxed" style="text-shadow: 0.5px 0.5px 0px #000;">${slide.text}</p>
            `;
        }
        if (tutSlideIndicator) {
            tutSlideIndicator.textContent = `${currentTutorialSlide + 1} / ${tutorialSlides.length}`;
        }
        if (tutPrevBtn) tutPrevBtn.disabled = currentTutorialSlide === 0;
        if (tutNextBtn) {
            if (currentTutorialSlide === tutorialSlides.length - 1) {
                tutNextBtn.textContent = "Finish";
            } else {
                tutNextBtn.textContent = "Next";
            }
        }
    }
    
    tutorialModeBtn.addEventListener('click', () => {
        playMenuClickSound();
        currentTutorialSlide = 0;
        updateSlide();
        tutorialModal.classList.add('active');
    });
    
    const closeBtnHandler = () => {
        playMenuClickSound();
        tutorialModal.classList.remove('active');
    };
    
    if (closeTutorialBtn) closeTutorialBtn.addEventListener('click', closeBtnHandler);
    if (document.getElementById('closeTutorialBtn')) document.getElementById('closeTutorialBtn').addEventListener('click', closeBtnHandler);
    
    if (tutPrevBtn) {
        tutPrevBtn.addEventListener('click', () => {
            if (currentTutorialSlide > 0) {
                playMenuClickSound();
                currentTutorialSlide--;
                updateSlide();
            }
        });
    }
    
    if (tutNextBtn) {
        tutNextBtn.addEventListener('click', () => {
            if (currentTutorialSlide < tutorialSlides.length - 1) {
                playMenuClickSound();
                currentTutorialSlide++;
                updateSlide();
            } else {
                closeBtnHandler();
            }
        });
    }
}

export const ACHIEVEMENTS = {
    litterbug: {
        id: "litterbug",
        title: "Premature Yeet",
        desc: "Out of bounds on shot 1. Impressive.",
        icon: "🚮",
        anti: true
    },
    slingshot: {
        id: "slingshot",
        title: "Perfectly Balanced",
        desc: "Travel 3,000+ IAU only to miss the target completely.",
        icon: "⚖️",
        anti: false
    },
    muck: {
        id: "muck",
        title: "Beach Bum",
        desc: "Landed in the sand 3 times on one hole. Nice tan!",
        icon: "🏖️",
        anti: true
    },
    dizzy: {
        id: "dizzy",
        title: "Motion Sickness",
        desc: "Spun so fast you threw up green hurl. Classy.",
        icon: "🤢",
        anti: true
    },
    graze: {
        id: "graze",
        title: "Sub-Atomic Graze",
        desc: "Skimmed a surface at extreme speed. Show-off.",
        icon: "🔬",
        anti: false
    },
    pacifist: {
        id: "pacifist",
        title: "Cosmic Pacifist",
        desc: "Cleared a hole without touching any solid rock. Boring!",
        icon: "🕊️",
        anti: false
    },
    pachinko: {
        id: "pachinko",
        title: "Galactic Pachinko",
        desc: "It's not like I wanted you to land here or anything...",
        icon: "🎰",
        anti: false
    }
};

export function triggerAchievement(achId) {
    const ach = ACHIEVEMENTS[achId];
    if (!ach) return;
    
    const key = `spaceGolfAchievement_${achId}`;
    if (localStorage.getItem(key)) return;
    
    localStorage.setItem(key, "unlocked");
    console.log(`🏆 ACHIEVEMENT UNLOCKED: ${ach.title}`);
    
    // Play funny synth retro jingle via Tone.js
    try {
        const now = Tone.now();
        if (ach.anti) {
            const synth = new Tone.MonoSynth({
                oscillator: { type: "sawtooth" },
                filter: { Q: 2, type: "lowpass", frequency: 400 },
                envelope: { attack: 0.05, decay: 0.35, sustain: 0.1, release: 0.5 }
            }).toDestination();
            synth.volume.value = -3;
            synth.triggerAttackRelease("G2", "4n", now);
            synth.frequency.exponentialRampToValueAtTime(75, now + 0.45);
        } else {
            const synth = new Tone.PolySynth(Tone.Synth, {
                oscillator: { type: "triangle" },
                envelope: { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.2 }
            }).toDestination();
            synth.volume.value = -8;
            synth.triggerAttackRelease("C5", "16n", now);
            synth.triggerAttackRelease("E5", "16n", now + 0.08);
            synth.triggerAttackRelease("G5", "16n", now + 0.16);
            synth.triggerAttackRelease("C6", "8n", now + 0.24);
        }
    } catch(e) {
        console.warn("Tone.js achievement audio failed:", e);
    }
    
    // Render sliding pop up card
    const container = document.getElementById('achievementNotification');
    if (!container) return;
    
    const card = document.createElement('div');
    card.className = `achievement-card ${ach.anti ? 'anti' : ''}`;
    card.innerHTML = `
        <div class="achievement-icon">${ach.icon}</div>
        <div class="achievement-text">
            <h4 class="achievement-title">${ach.anti ? '⚠️ ACHIEVEMENT UNLOCKED' : '🏆 ACHIEVEMENT UNLOCKED'}</h4>
            <div class="font-orbitron font-black text-xs text-white mb-0.5" style="text-shadow: 1px 1px 0px #000;">${ach.title}</div>
            <p class="achievement-desc">${ach.desc}</p>
        </div>
    `;
    
    container.appendChild(card);
    
    // Bouncy slide in
    setTimeout(() => {
        card.classList.add('active');
    }, 50);
    
    // Snug remove
    setTimeout(() => {
        card.classList.remove('active');
        setTimeout(() => {
            card.remove();
        }, 500);
    }, 4500);
}

